import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Skeleton, Tag } from "antd";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";

interface DiagnosticCheck {
  code: string;
  title: string;
  status: "pass" | "warning" | "error" | "pending";
  detail: string;
  action?: string;
  evidence?: string;
}

interface DiagnosticReport {
  status: "healthy" | "attention" | "not_connected";
  origin?: string;
  checkedAt: string;
  checks: DiagnosticCheck[];
}

const statusMeta = {
  pass: { icon: "ri-checkbox-circle-fill", label: "通过", color: "success" as const },
  warning: { icon: "ri-error-warning-fill", label: "注意", color: "warning" as const },
  error: { icon: "ri-close-circle-fill", label: "需修复", color: "error" as const },
  pending: { icon: "ri-time-fill", label: "待检测", color: "default" as const },
};

const InstallDiagnostics: React.FC<{ siteId: string }> = ({ siteId }) => {
  const { API_BASE_URL } = useGlobal();
  const [report, setReport] = useState<DiagnosticReport>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const diagnose = useCallback(async (signal?: AbortSignal) => {
    if (!siteId) return;
    setLoading(true);
    setError("");
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/install-diagnostics`, { signal });
      if (response.data?.code !== 200) throw new Error(response.data?.msg || "诊断失败");
      setReport(response.data.data);
    } catch (reason: any) {
      if (reason?.code !== "ERR_CANCELED") setError(reason.response?.data?.msg || reason.message || "诊断失败，请稍后重试");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_BASE_URL, siteId]);

  useEffect(() => {
    const controller = new AbortController();
    void diagnose(controller.signal);
    return () => controller.abort();
  }, [diagnose]);

  if (!siteId) return <Alert showIcon type="info" message="创建网站并保存安装方式后，即可运行自动验证。" />;
  if (loading && !report) return <Skeleton active paragraph={{ rows: 5 }} />;
  if (error) return <Alert showIcon type="error" message="安装诊断未完成" description={error} action={<Button size="small" onClick={() => void diagnose()}>重试</Button>} />;

  return (
    <div className="install-diagnostics">
      <header>
        <div>
          <strong>{report?.status === "healthy" ? "安装状态正常" : report?.status === "not_connected" ? "尚未连接网页" : "发现需要处理的项目"}</strong>
          <span>{report?.origin || "production"} · {report?.checkedAt ? new Date(report.checkedAt).toLocaleTimeString() : "待检测"}</span>
        </div>
        <Button loading={loading} icon={<i className="ri-refresh-line" />} onClick={() => void diagnose()}>重新检测</Button>
      </header>
      <div className="install-diagnostics__checks">
        {(report?.checks || []).map((check) => {
          const meta = statusMeta[check.status];
          return (
            <article key={check.code} className={`diagnostic-check diagnostic-check--${check.status}`}>
              <i className={meta.icon} />
              <div>
                <strong>{check.title}</strong>
                <p>{check.detail}</p>
                {check.action && check.status !== "pass" ? <small><b>怎么修：</b>{check.action}</small> : null}
                {check.evidence ? <small><b>依据：</b>{check.evidence}</small> : null}
              </div>
              <Tag color={meta.color}>{meta.label}</Tag>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default InstallDiagnostics;
